import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AuthenticationService } from '../../authentication/shared/authentication.service';
import { StorageService } from '../../shared/storage/storage.service';
import { SignInModel } from '../../authentication/shared/sign-in-model';
import { ROUTE_PRIVACY_STATEMENT, ROUTE_TERMS_CONDITIONS } from '../../authentication/shared/routes';
import { ROUTE_DRIVER_JOB, ROUTE_DRIVER_JOB_SIGN_UP } from '../shared/routes';
import { PublicCarrier } from 'src/app/shared/models/public-carrier';
import { CarrierDataService } from 'src/app/shared/services/carrier-data.service';
import { RecaptchaEnterpriseService } from 'src/app/shared/recaptcha-enterprise/recaptcha-enterprise.service';
import { ToastService } from 'src/app/shared/toast/toast.service';
import {
  EMAIL_ALREADY_VERIFIED_CODE,
  RESEND_VERIFICATION_COOLDOWN_MS,
  RESEND_VERIFICATION_ERROR_MESSAGE,
  USER_NOT_CONFIRMED_CODE,
  VERIFICATION_EMAIL_SENT_CODE,
  VERIFICATION_EMAIL_SENT_MESSAGE,
  VERIFICATION_REQUIRED_MESSAGE
} from 'src/app/authentication/shared/email-verification';

const RequiredFieldMessage = 'You must enter a value';
const InvalidEmailAddress = 'Invalid email address';
const MinimumLengthFieldMessage = (value: number) => `Must be at least ${value} characters`;
const SignInForm = 'SignFormDetails';

@Component({
    selector: 'app-sign-in',
    templateUrl: './sign-in.component.html',
    styleUrls: ['./sign-in.component.scss'],
    standalone: false
})
export class SignInComponent implements OnInit, AfterViewInit, OnDestroy {

  form: UntypedFormGroup;
  hidePassword: boolean = true;
  loading: boolean = false;
  errorMessage: string;
  verificationRequired = false;
  resendVerificationLoading = false;
  resendVerificationCooldown = false;
  carrierCompanyId: string = null;
  publishableKey: string = null;
  carrierData: PublicCarrier = null;
  @ViewChild('email', { static: false }) emailInput: any;

  private subs = new SubSink();
  private resendCooldownTimer: ReturnType<typeof setTimeout>;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly authenticationApi: AuthenticationService,
    private readonly elementRef: ElementRef,
    private readonly recaptchaEnterpriseService: RecaptchaEnterpriseService,
    private readonly storageService: StorageService,
    private readonly carrierDataService: CarrierDataService,
    private readonly toastService: ToastService
  ) {
    this.form = this.createForm();
  }

  ngOnInit(): void {
    this.carrierCompanyId = sessionStorage.getItem('carrierCompanyId');
    this.publishableKey = sessionStorage.getItem('publishableKey');

    if (this.carrierCompanyId && this.publishableKey) {
      this.loadCarrierData(this.carrierCompanyId);
    }
  }

  ngAfterViewInit(): void {
    this.elementRef.nativeElement.ownerDocument.body.style.backgroundColor = '#fff';

    setTimeout(() => {
      this.emailInput?.nativeElement?.focus();
    }, 0);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    clearTimeout(this.resendCooldownTimer);
    this.elementRef.nativeElement.ownerDocument.body.style.backgroundColor = '#fff';
  }

  loadCarrierData(companyId: string): void {
    this.carrierDataService.getCarrierPublicById(companyId).subscribe({
      next: (response) => {
        this.carrierData = response;
      },
      error: (err) => {
        console.error('Error fetching carrier data', err);
      }
    });
  }

  onSubmit(signinForm: NgForm): void {
    if (!this.form.valid) return;

    this.loading = true;
    const model = this.prepareSaveModel();

    this.subs.add(
      this.recaptchaEnterpriseService
        .execute('login')
        .pipe(
          switchMap(token => this.authenticationApi.login(model, token)),
          catchError((error: any) => {
            if (error?.code === USER_NOT_CONFIRMED_CODE) {
              this.verificationRequired = true;
              this.errorMessage = VERIFICATION_REQUIRED_MESSAGE;
            } else {
              this.verificationRequired = false;
              this.errorMessage = error?.message;
            }
            this.loading = false;
            return of(false);
          })
        )
        .subscribe()
    );
  }

  resendVerificationEmail(): void {
    if (this.resendVerificationLoading || this.resendVerificationCooldown) {
      return;
    }

    const username = (this.email.value as string).trim();
    this.resendVerificationLoading = true;
    this.startResendCooldown();

    this.subs.add(
      this.authenticationApi.resendVerificationEmail(username).subscribe({
        next: response => {
          this.resendVerificationLoading = false;

          if (response.code === VERIFICATION_EMAIL_SENT_CODE) {
            this.toastService.showSuccess(VERIFICATION_EMAIL_SENT_MESSAGE);
          } else if (response.code === EMAIL_ALREADY_VERIFIED_CODE) {
            this.verificationRequired = false;
            this.errorMessage = undefined;
            this.toastService.showInfo('Your email is already verified. Please sign in.');
          } else {
            this.toastService.showError(RESEND_VERIFICATION_ERROR_MESSAGE);
          }
        },
        error: () => {
          this.resendVerificationLoading = false;
          this.toastService.showError(RESEND_VERIFICATION_ERROR_MESSAGE);
        }
      })
    );
  }

  get email() {
    return this.form.get('email');
  }

  get password() {
    return this.form.get('password');
  }

  getEmailErrorMessage() {
    const errors = this.email.errors;
    return errors?.required
      ? RequiredFieldMessage
      : errors?.pattern
        ? InvalidEmailAddress
        : '';
  }

  getPasswordErrorMessage() {
    const errors = this.password.errors;
    return errors?.required
      ? RequiredFieldMessage
      : errors?.minlength
        ? MinimumLengthFieldMessage(errors.minlength.requiredLength)
        : '';
  }

  private createForm(): UntypedFormGroup {
    return this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\\.[A-Za-z0-9]+)((\\.[A-Za-z]{2,}){0,1})\\s*$')
        ]
      ],
      password: ['', [Validators.required, Validators.minLength(8)]]
    });
  }

  private prepareSaveModel(): SignInModel {
    const formModel = this.form.value;
    return {
      username: formModel.email.trim(),
      password: formModel.password
    } as SignInModel;
  }

  onNavigateToSignUp() {
    this.router.navigate([`/${ROUTE_DRIVER_JOB}`, `${ROUTE_DRIVER_JOB_SIGN_UP}`]);
  }

  onNavigateToTermsConditions() {
    this.storageService.store(SignInForm, this.prepareSaveModel());
    this.router.navigate([`/${ROUTE_TERMS_CONDITIONS}`]);
  }

  onNavigateToPrivacyStatement() {
    this.storageService.store(SignInForm, this.prepareSaveModel());
    this.router.navigate([`/${ROUTE_PRIVACY_STATEMENT}`]);
  }

  private startResendCooldown(): void {
    this.resendVerificationCooldown = true;
    clearTimeout(this.resendCooldownTimer);
    this.resendCooldownTimer = setTimeout(() => {
      this.resendVerificationCooldown = false;
    }, RESEND_VERIFICATION_COOLDOWN_MS);
  }
}